import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from "@stencil/core";

import { StzhAccordionItemOpenEvent, StzhAccordionVariant, StzhAccordionVariantChangeEvent } from "../../index";

/**
 * @slot - Slot for `stzh-accordion-item` elements
 */
@Component({
  tag: "stzh-accordion",
  styleUrl: "stzh-accordion.scss",
  shadow: true,
})
export class StzhAccordion {
  /** Variant */
  @Prop({ reflect: true }) variant: StzhAccordionVariant = "default";

  /** Heading */
  @Prop({ reflect: true }) heading: string = "";

  /** Whether folds are collapsible */
  @Prop() collapsible: boolean = true;

  /** Whether multiple folds can be opened at once */
  @Prop() multiselectable: boolean = true;

  @Element() element: HTMLStzhAccordionElement;

  /** Variant change event */
  @Event() stzhVariantChange: EventEmitter<StzhAccordionVariantChangeEvent>;

  @State() isNested: boolean;

  @Watch("variant")
  variantWatcher(newValue: StzhAccordionVariant) {
    this.stzhVariantChange.emit({
      component: "stzh-accordion",
      value: newValue,
    });
  }

  @Listen("stzhOpen")
  openListener(event: CustomEvent<StzhAccordionItemOpenEvent>) {
    const accordionItemOpened = event.target as HTMLStzhAccordionItemElement;
    const accordionOpened = accordionItemOpened.closest("stzh-accordion");

    if (
      this.multiselectable ||
      (event.detail && event.detail.component !== "stzh-accordion-item") ||
      accordionOpened !== this.element
    ) {
      return;
    }

    const items: HTMLStzhAccordionItemElement[] = Array.from(accordionOpened.querySelectorAll("stzh-accordion-item"));
    items.forEach(item => {
      if (item !== accordionItemOpened) {
        item.hide();
      }
    });
  }

  /** Set focus to accordion item */
  @Method()
  async setItemFocus(accordionItem: HTMLStzhAccordionItemElement, action: string) {
    const accordion = accordionItem.closest("stzh-accordion");

    if (accordion !== this.element) {
      return;
    }

    const items = (
      Array.from(accordion.querySelectorAll(":scope > stzh-accordion-item")) as HTMLStzhAccordionItemElement[]
    ).filter(item => !item.disabled);

    const currentIndex = items.indexOf(accordionItem);

    if ((action === "prev" || action === "next") && currentIndex === null) {
      action = action === "prev" ? "last" : "first";
    }

    if (action === "prev" && currentIndex === 0) {
      action = "last";
    }

    if (action === "next" && currentIndex === items.length - 1) {
      action = "first";
    }

    if (action === "prev") {
      items[currentIndex - 1].focus();
    } else if (action === "next") {
      items[currentIndex + 1].focus();
    } else if (action === "last") {
      items[items.length - 1].focus();
    } else if (action === "first") {
      items[0].focus();
    } else {
      accordionItem.focus();
    }
  }

  private checkIfNested() {
    let parentElement = this.element.parentElement;

    while (parentElement) {
      if (parentElement.tagName.toLowerCase() === "stzh-accordion") {
        this.isNested = true;
        break;
      }
      parentElement = parentElement.parentElement;
    }
  }

  componentDidLoad() {
    this.checkIfNested();
  }

  render() {
    const classes = {
      "stzh-accordion": true,
      "stzh-accordion--is-nested": this.isNested,
    };

    return (
      <Host>
        <div class={classes}>
          {this.heading && (
            <div class="stzh-accordion__heading">
              <stzh-heading level="2">{this.heading}</stzh-heading>
            </div>
          )}
          <slot></slot>
        </div>
      </Host>
    );
  }
}
